/**
 * NEXUMA — Chrome i përbashkët i shablloneve
 *
 * Tre panelet (kontrata · vendime · vërejtje) ishin shkruar veç e veç dhe kishin
 * dalë tre gjuhë vizuale: njëri me kokë `h6/800` pa kartelë, dy të tjerët me
 * Card + shirit koke; kërkimi një herë me `<svg>` të shkruar me dorë, një herë me
 * ikonë MUI, një herë fare; butoni "Shto" i kuq, blu ose i markës; dhe 17 hex-e
 * të hardkoduara mes tyre. Kalimi nga një segment te tjetri e ndërronte gjuhën
 * e faqes.
 *
 * Këtu është një gjuhë e vetme, aq e hollë sa panelet ta mbajnë secili logjikën
 * e vet: shirit veglash → rrjetë kartelash → kartelë. Kartela e panelit u hoq —
 * brenda tabit "Shabllonet" te Konfigurimi ajo ishte kartelë brenda kartele, dhe
 * titulli i saj përsëriste segmentin e zgjedhur pak pikselë më lart.
 *
 * Ngjyra: theksi është marka. Ngjyra semantike (e kuqja, portokallia) i mbetet
 * VETËM ashpërsisë së vërejtjeve, e cila është status i vërtetë — jo llojit të
 * shabllonit. Prandaj vendimet nuk janë më të kuqe: nuk ishin gabim, dukeshin.
 */
import React from 'react';
import {
  Box, Stack, Typography, TextField, InputAdornment, IconButton,
  Tooltip, CircularProgress, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

/** Lartësia e përbashkët e kontrolleve — një rresht i vetëm veglash, 30px. */
const CONTROL_H = 30;

/**
 * Shirit veglash: shpjegimi majtas, filtrat e panelit në mes, kërkimi dhe
 * veprimi kryesor djathtas. Pa kornizë — e ndan vetëm një vijë poshtë, që të
 * mos shtohet edhe një kuti brenda kutisë së tabit.
 */
export function TemplateToolbar({ icon, title, hint, search, onSearch, searchLabel = 'Kërko…', children, action }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ pb: 1.1, mb: 1.25, borderBottom: '1px solid', borderColor: 'divider', rowGap: 1 }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 'auto', minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              width: 26, height: 26, flexShrink: 0, borderRadius: 1.25,
              display: 'grid', placeItems: 'center',
              bgcolor: 'primary.soft', color: 'primary.main',
              '& svg': { fontSize: 15 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          {title && (
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.25 }}>
              {title}
            </Typography>
          )}
          {hint && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.35, maxWidth: 460 }}
            >
              {hint}
            </Typography>
          )}
        </Box>
      </Stack>
      {children}
      {onSearch && (
        <TextField
          size="small"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchLabel}
          inputProps={{ 'aria-label': searchLabel }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Pastro kërkimin" onClick={() => onSearch('')}>
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: { xs: '100%', sm: 190 },
            '& .MuiOutlinedInput-root': { height: CONTROL_H, fontSize: '0.78rem', borderRadius: 1.5 },
          }}
        />
      )}
      {action}
    </Stack>
  );
}

/** sx i përbashkët i butonit kryesor, që të tre panelet ta kenë të njëjtin. */
export const templateActionSx = {
  height: CONTROL_H,
  px: 1.5,
  borderRadius: 1.5,
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '&:hover': { boxShadow: 'none' },
};

/** Rrjeta e kartelave — e njëjta dendësi për të tre llojet. */
export function TemplateGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(248px, 1fr))' },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Gjendjet para përmbajtjes: ngarkim → gabim → zbrazëti → përmbajtje.
 *
 * I MBËSHTJELL fëmijët në vend që të kthejë `null` kur s'ka gjendje. Varianti i
 * parë kthente `null` dhe panelet e përdornin si `{state || <Grid/>}` — por një
 * element JSX është gjithmonë truthy edhe kur komponenti brenda tij vizaton
 * `null`, ndaj `||` nuk binte kurrë te rrjeta dhe kartelat nuk dilnin fare.
 * Me fëmijët brenda, ai gabim nuk shkruhet dot.
 *
 * Gabimi i ngarkimit del gjithmonë — një server i rënë nuk guxon të duket si
 * listë e zbrazët.
 */
export function TemplateState({ loading, error, empty, loadingNode, children }) {
  if (loading) {
    return loadingNode || (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (error) return <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>;
  if (empty) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">{empty}</Typography>
      </Box>
    );
  }
  return children || null;
}

/**
 * Kartelë shablloni. `accent` është buza 3px majtas: marka për kontratat e
 * vendimet, ngjyra e nivelit vetëm për vërejtjet. `dim` = i çaktivizuar.
 */
export function TemplateCard({ accent, dim = false, title, titleAdornment, aside, children, actions }) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 0.6,
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: dim ? theme.palette.grey[300] : (accent || theme.palette.primary.main),
        bgcolor: dim ? 'grey.50' : 'background.paper',
        transition: theme.motion?.surface,
        '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.06)' },
      })}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={typeof title === 'string' ? title : undefined}
            sx={{
              fontWeight: 700,
              fontSize: '0.8rem',
              lineHeight: 1.3,
              color: dim ? 'text.disabled' : 'text.primary',
            }}
          >
            {title}
          </Typography>
          {titleAdornment}
        </Box>
        {aside && <Box sx={{ flexShrink: 0 }}>{aside}</Box>}
      </Stack>

      {children}

      {actions && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 'auto', pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
        >
          {actions}
        </Stack>
      )}
    </Box>
  );
}

/** Tekst i gjatë i kartelës, i prerë pas `lines` rreshtash. */
export function TemplateText({ children, lines = 2, muted = false }) {
  return (
    <Typography
      sx={{
        fontSize: '0.72rem',
        lineHeight: 1.5,
        color: muted ? 'text.disabled' : 'text.secondary',
        fontStyle: muted ? 'italic' : 'normal',
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {children}
    </Typography>
  );
}

/** Veprim i kartelës — 26px, ton neutral derisa të preket. */
export function TemplateAction({ label, icon, danger = false, ...rest }) {
  return (
    <Tooltip title={label} arrow>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          {...rest}
          sx={{
            width: 26,
            height: 26,
            color: 'text.secondary',
            '&:hover': danger
              ? { bgcolor: 'error.soft', color: 'error.onSoft' }
              : { bgcolor: 'primary.soft', color: 'primary.main' },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}
